import type { ContentBlock, ContentPackage, ContentSection } from './types';

export const CONTENT_URL = '/content/np-t-2.7/v0/content.json';
export const SOURCE_PDF_URL = '/content/np-t-2.7/v0/source.pdf';
export const INTRODUCTION_SECTION_ID = 'section-1';
export const EXCLUDED_SECTION_IDS = ['front-matter', 'contributors-to-drafting-and-review'] as const;
export const EXCLUDED_BLOCK_KINDS = ['page-marker', 'figure'] as const;

export interface ReaderExclusion {
  type: 'section' | 'block-kind';
  id: string;
  reason: string;
}

export const READER_EXCLUSIONS: ReaderExclusion[] = [
  { type: 'section', id: 'front-matter', reason: 'Материал до 1. INTRODUCTION не входит в маршрут чтения.' },
  { type: 'section', id: 'contributors-to-drafting-and-review', reason: 'Служебный список составителей исключён из основного чтения.' },
  { type: 'block-kind', id: 'page-marker', reason: 'Отдельные номера страниц и колонтитулы не являются текстом книги.' },
  { type: 'block-kind', id: 'figure', reason: 'Изображения и схемы исключены; авторские текстовые ссылки на них сохраняются в paragraphs.' },
  { type: 'block-kind', id: 'table-caption:FIG.', reason: 'В текущем пакете 33 блока kind=table являются транскрипциями схем FIG.; содержательные TABLE-блоки сохраняются.' },
];

export async function loadContent(signal?: AbortSignal): Promise<ContentPackage> {
  const response = await fetch(CONTENT_URL, { signal, cache: 'no-cache' });
  if (!response.ok) throw new Error(`Контент недоступен: ${response.status}`);
  const value = await response.json() as ContentPackage;
  if (value.formatId !== 'npt27-content-package' || !Array.isArray(value.blocks) || !Array.isArray(value.sections)) {
    throw new Error('Контент-пакет имеет неподдерживаемую структуру');
  }
  return value;
}

function sectionMap(content: ContentPackage): Map<string, ContentSection> {
  return new Map(content.sections.map((section) => [section.id, section]));
}

export function sectionDepth(content: ContentPackage, sectionId: string): number {
  const sections = sectionMap(content);
  let depth = 0;
  let current = sections.get(sectionId);
  const seen = new Set<string>();
  while (current?.parentId && !seen.has(current.id)) {
    seen.add(current.id);
    depth += 1;
    current = sections.get(current.parentId);
  }
  return depth;
}

export function isReadableSection(content: ContentPackage, sectionId: string): boolean {
  const intro = sectionById(content, INTRODUCTION_SECTION_ID);
  const section = sectionById(content, sectionId);
  if (!intro || !section || EXCLUDED_SECTION_IDS.includes(section.id as typeof EXCLUDED_SECTION_IDS[number])) return false;
  return section.order >= intro.order;
}

export function isReadableBlock(content: ContentPackage, block: ContentBlock): boolean {
  return isReadableSection(content, block.sectionId)
    && !EXCLUDED_BLOCK_KINDS.includes(block.kind as typeof EXCLUDED_BLOCK_KINDS[number])
    && !(block.kind === 'table' && /^FIG\./i.test((block.semanticTable?.caption ?? block.enText).trim()));
}

export function readerSections(content: ContentPackage): ContentSection[] {
  return content.sections
    .filter((section) => isReadableSection(content, section.id))
    .sort((a, b) => a.order - b.order);
}

export function topLevelReaderSections(content: ContentPackage): ContentSection[] {
  return readerSections(content).filter((section) => section.parentId === null);
}

export function sectionDescendants(content: ContentPackage, sectionId: string): ContentSection[] {
  const result: ContentSection[] = [];
  const sections = readerSections(content);
  const visit = (parentId: string) => {
    for (const section of sections.filter((candidate) => candidate.parentId === parentId)) {
      result.push(section);
      visit(section.id);
    }
  };
  if (isReadableSection(content, sectionId)) visit(sectionId);
  return result;
}

export function chapterForSection(content: ContentPackage, sectionId: string): ContentSection | undefined {
  const sections = sectionMap(content);
  let current = sections.get(sectionId);
  const seen = new Set<string>();
  while (current?.parentId && !seen.has(current.id)) {
    seen.add(current.id);
    current = sections.get(current.parentId);
  }
  return current && isReadableSection(content, current.id) ? current : undefined;
}

export function blocksForSections(content: ContentPackage, sectionIds: Set<string>): ContentBlock[] {
  const seen = new Set<string>();
  return content.blocks
    .filter((block) => sectionIds.has(block.sectionId) && isReadableBlock(content, block))
    .sort((a, b) => a.order - b.order)
    .filter((block) => {
      if (seen.has(block.id)) return false;
      seen.add(block.id);
      return true;
    });
}

export function blocksForChapter(content: ContentPackage, chapterId: string): ContentBlock[] {
  const ids = new Set([chapterId, ...sectionDescendants(content, chapterId).map((section) => section.id)]);
  return blocksForSections(content, ids);
}

export function visibleBlocks(content: ContentPackage): ContentBlock[] {
  return blocksForSections(content, new Set(readerSections(content).map((section) => section.id)));
}

export function firstReadableBlock(content: ContentPackage): ContentBlock | undefined {
  return visibleBlocks(content)[0];
}

export function firstBlockForSection(content: ContentPackage, sectionId: string): ContentBlock | undefined {
  const chapter = chapterForSection(content, sectionId);
  if (!chapter) return undefined;
  return blocksForSections(content, new Set([sectionId]))[0]
    ?? blocksForChapter(content, chapter.id)[0];
}

export function getReadingPages(content: ContentPackage): number[] {
  return [...new Set(content.blocks
    .flatMap((block) => block.sourceRefs.map((ref) => ref.pdfPageNumber))
    )].sort((a, b) => a - b);
}

export function blocksForPage(content: ContentPackage, pageNumber: number): ContentBlock[] {
  return content.blocks.filter((block) => block.sourceRefs.some((ref) => ref.pdfPageNumber === pageNumber));
}

export function sectionForPage(content: ContentPackage, pageNumber: number): ContentSection {
  return content.sections.find((section) => pageNumber >= section.pdfPageFrom && pageNumber <= section.pdfPageTo)
    ?? sectionById(content, INTRODUCTION_SECTION_ID)
    ?? content.sections[0];
}

export function sectionById(content: ContentPackage, sectionId: string): ContentSection | undefined {
  return content.sections.find((section) => section.id === sectionId);
}

export function blockById(content: ContentPackage, blockId: string): ContentBlock | undefined {
  return content.blocks.find((block) => block.id === blockId);
}

export function normalizeSearch(value: string): string {
  return value.normalize('NFC').toLocaleLowerCase('ru-RU').replaceAll('ё', 'е').replace(/\s+/g, ' ').trim();
}

export interface SearchHit {
  block: ContentBlock;
  section: ContentSection;
  language: 'en' | 'ru';
  fragment: string;
}

export function searchContent(content: ContentPackage, query: string, limit = 40): SearchHit[] {
  const normalized = normalizeSearch(query);
  if (normalized.length < 2) return [];
  const hits: SearchHit[] = [];
  for (const block of visibleBlocks(content)) {
    const en = normalizeSearch(block.enText);
    const ru = normalizeSearch(block.ruText);
    const section = sectionById(content, block.sectionId) ?? content.sections[0];
    if (en.includes(normalized)) hits.push({ block, section, language: 'en', fragment: compact(block.enText) });
    else if (ru.includes(normalized)) hits.push({ block, section, language: 'ru', fragment: compact(block.ruText) });
    if (hits.length >= limit) break;
  }
  return hits;
}

export function compact(value: string, length = 170): string {
  const clean = value.replace(/\s+/g, ' ').trim();
  return clean.length > length ? `${clean.slice(0, length - 1)}…` : clean;
}

export function sourceLabel(block: ContentBlock): string {
  const pages = [...new Set(block.sourceRefs.map((ref) => ref.pdfPageNumber))].sort((a, b) => a - b);
  return pages.length === 1 ? `PDF page ${pages[0]}` : `PDF pages ${pages.join('–')}`;
}

export function readingGroups(blocks:ContentBlock[]):ContentBlock[][] {
  const groups:ContentBlock[][]=[];
  for(const block of blocks){const last=groups.at(-1);if(block.kind==='list-item'&&last?.[0].kind==='list-item')last.push(block);else groups.push([block]);}
  return groups;
}
